import { ColumnsType } from "antd/es/table"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { SystemDescriptions } from "src/common/descriptions"
import { ServiceContract } from "src/common/models/contract"
import { ServiceDebitContract } from "src/common/models/serviceContractDebit"
import { SearchableTable } from "src/components/SearchableTable"
import { cleanCurrentContract, getContractDebitByIdApi, getServiceContractByIdApi } from "src/features/ServiceContract/state/action"
import { GET_CONTRACT_DEBIT_BY_ID_API, GET_SERVICE_CONTRACT_DEBITS_API } from "src/features/ServiceContract/state/actionTypes"
import { RootState } from "src/state/reducer"
import { ColumnsKeys, summaryColumns, TABLE_COLUMNS } from "./config"
import { searchableTableUtils } from "src/components/SearchableTable/utils"
import { Button, Space, Tooltip, notification } from "antd"
import { ProfileOutlined, CreditCardOutlined } from '@ant-design/icons'
import { ContractConfirmation } from "src/features/ServiceContract/CreateServiceContract/ContractConfirmation"
import { CreatePayment } from "src/features/ServiceContract/CreateServiceContract/TicketSetction/CreatePayment"
import { Loader } from "src/components/Loader"
import { useParams } from "react-router"
import { getPayableContractDebitsApi } from "src/features/AccountPayable/state/action"
import { PayableContractDebit, PayableContractDebitRecordsFilters } from "src/common/models/payableContractDebit"
import dayjs from 'dayjs'
import moment from "moment"
import { SystemConstants } from "src/common/constants"
import { DateRanges } from "src/components/SearchableTable/types"

export type ReduxProps = {
    payableContractDebits?: PayableContractDebit[],
    isGettingContractDebits: boolean
    currentContract?: Partial<ServiceContract>
    isGettingContract: boolean
    isGettingContractDebit: boolean
    currentContractDebit?: ServiceDebitContract
}

interface Props {
    payableContractId?: number
}

export const PayableContractDebitsComponent = (props: Props) => {
    const dispatch = useDispatch()
    const { id: supplierId } = useParams<{ id: string }>();

    const [selectedAction, setSelectedAction] = useState<string | undefined>()

    useEffect(() => {
        initData()
    }, [])

    const [dateRanges, setDateRanges] = useState<DateRanges>({
        startDate: supplierId
            ? moment().subtract(3, 'month').startOf('month').format(SystemConstants.DATETIME_QUERY(true))
            : moment().startOf('month').format(SystemConstants.DATETIME_QUERY(true)),
        endDate: moment().format(SystemConstants.DATETIME_QUERY(false)),
    })

    const initData = () => {
        const payload: PayableContractDebitRecordsFilters = {
            dateFrom: dateRanges.startDate,
            dateTo: dateRanges.endDate,
        }
        if (props.payableContractId) {
            payload.payableContractId = props.payableContractId
        } else if (supplierId) {
            payload.supplierId = Number(supplierId)
        }

        dispatch(getPayableContractDebitsApi(payload))
    }

    const descriptions = SystemDescriptions.PAGES.SERVICE_CONTRACT.SERVICE_CONTRACT_DEBITS
    const commonDescriptions = SystemDescriptions.PAGES.COMMON
    const createDescriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CREATE_PAYMENT

    const reduxProps: ReduxProps = useSelector((state: RootState) => {
        return ({
            payableContractDebits: state.accountPayables.payableContractDebits || [],
            isGettingContractDebits: serviceLoading(state, [GET_SERVICE_CONTRACT_DEBITS_API]),
            currentContract: state.serviceContract.currentContract,
            isGettingContract: serviceLoading(state, [GET_CONTRACT_DEBIT_BY_ID_API]),
            currentContractDebit: state.serviceContract.currentContractDebit,
            isGettingContractDebit: serviceLoading(state, [GET_CONTRACT_DEBIT_BY_ID_API]),
        })
    })

    const tblColumns: ColumnsType<ServiceContract> = [
        ...TABLE_COLUMNS,
        {
            title: "actions",
            dataIndex: ColumnsKeys.ACTIONS,
            key: ColumnsKeys.ACTIONS,
            align: searchableTableUtils.alignment.centerAlign,
            render: (_, record: ServiceDebitContract) => {
                const handleViewClick = (contractId: number) => {
                    setSelectedAction("VIEW")
                    dispatch(getServiceContractByIdApi(contractId))
                }

                const handleCreatePaymentClick = (contractId: number) => {
                    setSelectedAction("CREATE_PAYMENT")
                    dispatch(getContractDebitByIdApi(contractId))
                }

                return <Space size="middle">
                    <Tooltip title={descriptions.VIEW_CONTRACT} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<ProfileOutlined />}
                            onClick={() => handleViewClick(record.serviceContractId!)}
                        />
                    </Tooltip>
                    <Tooltip title={descriptions.CREATE_PAYMENT} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<CreditCardOutlined />}
                            onClick={() => handleCreatePaymentClick(record.id!)}
                        />
                    </Tooltip>
                </Space>
            },
        },
    ]

    const closeModal = () => {
        dispatch(cleanCurrentContract())
        setSelectedAction(undefined)
    }

    const openNotification = (message: string, description: string) => {
        notification.success({
            message,
            description,
        });
    };

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isGettingContractDebit}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    const handleDateRangesChange = (dateRanges: DateRanges) => {
        setDateRanges({
            startDate: moment(dateRanges.startDate).format(SystemConstants.DATETIME_QUERY(true)),
            endDate: moment(dateRanges.endDate).format(SystemConstants.DATETIME_QUERY(false)),
        })
    }

    const handleActionButtonClick = () => {
        initData()
    }

    return (
        <>
            <SearchableTable
                showSearch
                showLoader={reduxProps.isGettingContractDebits}
                items={reduxProps.payableContractDebits || []}
                tableColumns={tblColumns as any}
                searchLabel={descriptions.TABLE.SEARCH_PLACEHOLDER}
                summaryColumns={summaryColumns}
                showRangePicker
                rangePickerOnChange={handleDateRangesChange}
                rangePickerDefaultValues={[
                    supplierId ? dayjs().subtract(3, 'month').startOf('month') : dayjs().startOf('month'),
                    dayjs(),
                ]}
                actionButtonLabel={descriptions.TABLE.ACTION_BUTTON}
                showActionButton
                actionButtonOnClick={handleActionButtonClick}
            />
            {
                reduxProps.currentContract && selectedAction === "VIEW" &&
                <ContractConfirmation
                    open={!!reduxProps.currentContract}
                    onCancel={() => { closeModal() }}
                    onAccept={() => { closeModal() }}
                    okText={commonDescriptions.ACCEPT}
                    cancelText={commonDescriptions.CANCEL}
                    currentContract={reduxProps.currentContract}
                    type="SERVICE_CONTRACT"
                />
            }
            {
                reduxProps.currentContractDebit && selectedAction === "CREATE_PAYMENT" &&
                <CreatePayment
                    open={!!reduxProps.currentContractDebit}
                    onCancel={() => { closeModal() }}
                    onAccept={() => {
                        closeModal()
                        initData()
                        openNotification(
                            createDescriptions.SUCCESS_MODAL.TITLE,
                            createDescriptions.SUCCESS_MODAL.DESCRIPTION,
                        )
                    }}
                    contractDebit={reduxProps.currentContractDebit}
                />
            }
            {renderLoader()}
        </>
    )
}
